import type { Vector3 } from '@technology/web3d';
import {
  Accessor,
  createEffect,
  createMemo,
  createResource,
  on,
} from 'solid-js';
import { SharedCapabilityProviderBase } from '../base/shared-provider-base';
import { buildStationPathIndex } from './station-path-index';
import type {
  MissionPathRuns,
  PathGeometrySource,
  StationPathEntry,
  StationPathIndexResult,
  StationPathLocation,
} from './station-path-index.types';
import type { StationInfo } from './stations-provider';

interface PathLocationCache {
  /** Analysis output the locations were computed from; a new one invalidates them. */
  readonly geometryToken: Float64Array | undefined;
  readonly locations: Map<string, StationPathLocation>;
}

/**
 * Locates every station of a loaded mission along its mission path, so sync
 * can follow the path distance instead of the nearest station in 2D.
 */
export class StationPathIndexProvider extends SharedCapabilityProviderBase<StationPathEntry> {
  public readonly idKey = 'stationKey' as const;

  private readonly caches: Map<string, PathLocationCache>;
  private readonly index: Accessor<StationPathIndexResult>;

  constructor(
    private readonly stationsInfo: Accessor<StationInfo[]>,
    private readonly missions: Accessor<readonly MissionPathRuns[]>,
    private readonly geometry: PathGeometrySource
  ) {
    const caches = new Map<string, PathLocationCache>();
    const index = createMemo(() =>
      buildIndexWithCache(stationsInfo(), missions(), geometry, caches)
    );
    const entries = createMemo(() => index().entries);
    // eslint-disable-next-line solid/reactivity -- passes the accessor itself; the base reads it in tracked scopes
    super(entries);
    this.caches = caches;
    this.index = index;

    createEffect(on(this.index, (result) => this.releaseIndexedPaths(result)));

    createResource(
      () => {
        const paths = [...this.index().pathsMissingGeometry].sort((a, b) =>
          a.localeCompare(b)
        );
        return paths.length > 0 ? paths.join('\n') : false;
      },
      (paths) =>
        Promise.all(
          paths.split('\n').map((pathId) => this.geometry.restore(pathId))
        )
    );
  }

  public missionOfRun(runId: string): string | undefined {
    return this.missions().find((m) => m.runs.some((r) => r.runId === runId))
      ?.missionId;
  }

  public missionOfPath(pathId: string): string | undefined {
    return this.missions().find((m) => m.pathId === pathId)?.missionId;
  }

  public closestStationAtDistance(
    pathId: string,
    pathDistance: number
  ): StationPathEntry | undefined {
    let best: StationPathEntry | undefined;
    let bestGap = Infinity;
    for (const entry of this.items()) {
      if (entry.pathId !== pathId || entry.pathDistance === undefined) {
        continue;
      }
      const gap = Math.abs(entry.pathDistance - pathDistance);
      if (gap < bestGap) {
        best = entry;
        bestGap = gap;
      }
    }
    return best;
  }

  public closestStationInMission(
    missionId: string,
    position: Vector3
  ): StationPathEntry | undefined {
    const stations = this.stationsInfo();
    let best: StationPathEntry | undefined;
    let bestDistanceSq = Infinity;
    for (const entry of this.items()) {
      const station = stations[entry.stationIndex];
      if (entry.missionId !== missionId || !station) {
        continue;
      }
      const distanceSq = station.position.distanceToSquared(position);
      if (distanceSq < bestDistanceSq) {
        best = entry;
        bestDistanceSq = distanceSq;
      }
    }
    return best;
  }

  public async cleanup(): Promise<void> {
    this.caches.clear();
  }

  /**
   * Releases a mission path once each of its runs has stations in the index
   * and no station of the path waits for released vertices. Stations the
   * vertices cannot place (no path metadata, or a run outside every section)
   * stay without a path distance; keeping the vertices would not change that.
   */
  private releaseIndexedPaths(result: StationPathIndexResult): void {
    const indexedRuns = new Set(result.entries.map((e) => e.runId));
    for (const mission of this.missions()) {
      const { pathId } = mission;
      if (!pathId || result.pathsMissingGeometry.has(pathId)) {
        continue;
      }
      if (mission.runs.every((run) => indexedRuns.has(run.runId))) {
        this.geometry.release(pathId);
      }
    }
  }
}

// eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 17 > 15 allowed; refactor later
function buildIndexWithCache(
  stations: readonly StationInfo[],
  missions: readonly MissionPathRuns[],
  geometry: PathGeometrySource,
  caches: Map<string, PathLocationCache>
): StationPathIndexResult {
  const cache = new Map<string, StationPathLocation>();
  const livePaths = new Set<string>();
  for (const { pathId } of missions) {
    if (!pathId) {
      continue;
    }
    livePaths.add(pathId);
    const geometryToken =
      geometry.metadata(pathId)?.sectionChainages?.[0]?.segmentWeb3dCumDists;
    let pathCache = caches.get(pathId);
    if (!pathCache || pathCache.geometryToken !== geometryToken) {
      pathCache = { geometryToken, locations: new Map() };
      caches.set(pathId, pathCache);
    }
    pathCache.locations.forEach((location, key) => cache.set(key, location));
  }

  const result = buildStationPathIndex({
    stations,
    missions,
    pathMetadata: (pathId) => geometry.metadata(pathId),
    cache,
  });

  const indexedKeys = new Set(result.entries.map((e) => e.stationKey));
  for (const [pathId, pathCache] of caches) {
    if (!livePaths.has(pathId)) {
      caches.delete(pathId);
      continue;
    }
    for (const key of pathCache.locations.keys()) {
      if (!indexedKeys.has(key)) {
        pathCache.locations.delete(key);
      }
    }
  }
  for (const entry of result.entries) {
    const location = result.computed.get(entry.stationKey);
    if (location && entry.pathId) {
      caches.get(entry.pathId)?.locations.set(entry.stationKey, location);
    }
  }
  return result;
}
